// ─────────────────────────────────────────────────────────────────────────────
// auth/passkeys.js — every passkey ceremony the web Studio performs
//
// Each function is: ask the server for options → let the browser/OS show its
// passkey sheet → send the answer back. The server decides everything that
// matters (relying party studio.fennira.com, user verification required, …);
// this file only carries the messages and turns failures into sentences.
//
// Sign-up also solves an ALTCHA proof-of-work first. It runs invisibly with the
// browser's own crypto (no widget, nothing sent anywhere but our server) while
// the person reads the page, so it is usually done before they press the button.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";
import {
  startRegistration, startAuthentication, browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill, sendSignal, WebAuthnAbortService
} from "@simplewebauthn/browser";
import { solveChallenge, pbkdf2 } from "altcha/lib";
import { storeSession, authHeader } from "../authClient";

export const RP_ID = "studio.fennira.com";
export { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill };

/** An Error whose message can be shown to a person as is. */
export class PasskeyError extends Error {
  constructor(message, { cancelled = false, code } = {}) {
    super(message);
    this.cancelled = cancelled;
    this.code = code;
  }
}

function explain(err, fallback) {
  if (err instanceof PasskeyError) return err;
  const name = err?.name || "";
  const wcode = err?.code || "";
  if (name === "NotAllowedError" || wcode === "ERROR_CEREMONY_ABORTED" || name === "AbortError") {
    // Cancelled, timed out, or the person closed the sheet. Not an error worth shouting about.
    return new PasskeyError("Cancelled. Try again when you're ready.", { cancelled: true });
  }
  if (wcode === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") {
    return new PasskeyError("This device already has a passkey for your account.", { code: wcode });
  }
  if (wcode === "ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT" ||
      wcode === "ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT") {
    return new PasskeyError("This security key can't hold a passkey with a PIN or fingerprint. Use your phone or computer instead.", { code: wcode });
  }
  const server = err?.response?.data;
  if (server?.message) return new PasskeyError(server.message, { code: server.code });
  if (err?.response?.status === 429) return new PasskeyError("Too many attempts. Please wait a few minutes.");
  if (!err?.response && err?.request) return new PasskeyError("No connection. Check your internet and try again.");
  return new PasskeyError(fallback);
}

const withAuth = (extra = {}) => ({ headers: authHeader(), ...extra });

// ── Sign-up ──────────────────────────────────────────────────────────────────

/** Starts the proof-of-work. Returns a promise of the payload to send. */
export function prepareSignupProof() {
  return (async () => {
    const res = await axios.get("/api/auth/altcha");
    const challenge = res.data;
    const solution = await solveChallenge({ challenge, deriveKey: pbkdf2.deriveKey });
    if (!solution) throw new Error("no solution");
    return btoa(JSON.stringify({ challenge, solution }));
  })();
}

/**
 * Creates the account: proof-of-work → passkey → session.
 * Resolves { recoveryCode } — show it once and never store it.
 */
export async function signUpWithPasskey(proofPromise, name) {
  try {
    const altcha = await (proofPromise || prepareSignupProof());
    // The account's name is also the passkey's label in the password manager.
    const { data: opt } = await axios.post("/api/auth/passkey/register/options", { altcha, name });
    const response = await startRegistration({ optionsJSON: opt.options });
    const { data } = await axios.post("/api/auth/passkey/register/verify", {
      challengeId: opt.challengeId, response
    });
    storeSession(data);
    return { recoveryCode: data.recoveryCode };
  } catch (err) {
    throw explain(err, "We couldn't create your account. Please try again.");
  }
}

// ── Sign-in ──────────────────────────────────────────────────────────────────

/**
 * Passkey sign-in. `autofill` = conditional UI: the browser offers passkeys in
 * the username field's autofill and this promise waits until one is picked.
 * `phone` = the passkey is on a phone: the hint "hybrid" makes the browser go
 * straight to its QR code (scan with the phone, which checks over Bluetooth
 * that it is next to this computer, so a code sent by someone else can't be
 * used). Browsers without hints ignore it and show their usual choice.
 */
export async function signInWithPasskey({ autofill = false, phone = false } = {}) {
  let credentialId;
  try {
    const { data: opt } = await axios.post("/api/auth/passkey/login/options", {});
    const optionsJSON = phone ? { ...opt.options, hints: ["hybrid"] } : opt.options;
    const response = await startAuthentication({ optionsJSON, useBrowserAutofill: autofill });
    credentialId = response.id;
    const { data } = await axios.post("/api/auth/passkey/login/verify", {
      challengeId: opt.challengeId, response
    });
    storeSession(data);
    return data;
  } catch (err) {
    // A passkey this server no longer knows (deleted, or removed by "This
    // wasn't me"): ask the device to forget it, so it stops being offered.
    if (err?.response?.data?.code === "passkey_unknown" && credentialId) {
      sendSignal({ signalName: "unknownCredential", rpID: RP_ID, credentialID: credentialId }).catch(() => {});
    }
    throw explain(err, "We couldn't sign you in with that passkey.");
  }
}

export const cancelPasskeyPrompt = () => { try { WebAuthnAbortService.cancelCeremony(); } catch { /* none running */ } };

// ── Re-authentication (5 minutes, this session only) ─────────────────────────

export async function reauthWithPasskey() {
  try {
    const { data: opt } = await axios.post("/api/auth/reauth/options", {}, withAuth({ skipReauth: true }));
    const response = await startAuthentication({ optionsJSON: opt.options });
    await axios.post("/api/auth/reauth/verify", { challengeId: opt.challengeId, response }, withAuth({ skipReauth: true }));
    return true;
  } catch (err) {
    throw explain(err, "We couldn't confirm it's you.");
  }
}

export async function reauthWithPassword(password) {
  try {
    await axios.post("/api/auth/reauth/password", { password }, withAuth({ skipReauth: true }));
    return true;
  } catch (err) {
    throw explain(err, "We couldn't confirm it's you.");
  }
}

// ── Adding a passkey (also the recovery session's mandatory one) ────────────

/** → { passkey, recoveryCode? } — a recovery code comes back when a new one was issued. */
export async function addPasskey(name) {
  try {
    const { data: opt } = await axios.post("/api/auth/passkeys/add/options", {}, withAuth());
    const response = await startRegistration({ optionsJSON: opt.options });
    const { data } = await axios.post("/api/auth/passkeys/add/verify", {
      challengeId: opt.challengeId, response, name
    }, withAuth());
    return data;
  } catch (err) {
    throw explain(err, "We couldn't add the passkey.");
  }
}

// ── Recovery code ────────────────────────────────────────────────────────────

/** Opens a recovery session. → { restrictedUntil, passkeyDeadline } */
export async function recoverWithCode(code) {
  try {
    const { data } = await axios.post("/api/auth/recovery", { code });
    storeSession(data);
    return data;
  } catch (err) {
    if (err?.response?.status === 401) throw new PasskeyError("That recovery code didn't work. Check it and try again.");
    throw explain(err, "We couldn't check that code. Please try again.");
  }
}

/** Groups a typed code as XXXX-XXXX-… while the person types. */
export function formatCodeInput(value) {
  return String(value || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 24).match(/.{1,4}/g)?.join("-") || "";
}
